import moment from 'moment';
import { getLocalTime } from '../time/time';

export const getExpiryDatesWarningText = (productNamesToFillExpiryDates, isMarket) => {

	const expiredText = productNamesToFillExpiryDates?.expired?.length
		? `Products that expired or expire today:\n${formatProductList(productNamesToFillExpiryDates?.expired, productNamesToFillExpiryDates?.expired?.length)}\n\n`
		: '';

	const notFilledText = productNamesToFillExpiryDates?.notFilled?.length
		? `Products with unknown expiry dates${!isMarket ? ' or filled in incorrect order' : ''}:\n${formatProductList(productNamesToFillExpiryDates?.notFilled, productNamesToFillExpiryDates?.notFilled?.length)}\n\n`
		: '';

	const expirySoonText = productNamesToFillExpiryDates?.expirySoon?.length
		? `Products that expire soon:\n${formatProductList(productNamesToFillExpiryDates?.expirySoon, productNamesToFillExpiryDates?.expirySoon?.length)}\n\n`
		: '';

	const expirySoonerThanAVGDSLSText = productNamesToFillExpiryDates?.expirySoonerThanAVGDSLS?.length
		? `Products that may expire before the next run:\n${formatProductList(productNamesToFillExpiryDates?.expirySoonerThanAVGDSLS, productNamesToFillExpiryDates?.expirySoonerThanAVGDSLS?.length)}`
		: '';

	return `${expiredText}${notFilledText}${expirySoonText}${expirySoonerThanAVGDSLSText}`;
};

const formatProductList = (products = [], total = 0) => {

	if (!products.length) {
		return '';
	}

	const formattedList = products
		.slice(0, 4)
		.map(name => (name?.length > 35 ? `${name.substring(0, 35)}...` : name || 'No product'))
		.join(',\n');

	const moreText = total > 4 ? `\nand ${total - 4} product(s) more...` : '';

	return formattedList + moreText;
};

// Get counters that will block "confirm stock levels" if the driver does not update expiry dates
export const getExpiryDatesBlockers = (expiryDatesArray, tenantSettings, {
	referenceDate,
	timezone,
} = {}) => {
	const getDayMoment = value => (
		timezone
			? getLocalTime(timezone, value).startOf('day')
			: moment(value).startOf('day')
	);
	const currentDate = getDayMoment(referenceDate || new Date());

	let expiredProductsCount = 0;
	let expireTodayCount = 0;
	let hasExpiryDatesNotFilledCount = 0;
	let productsPlacedInIncorrectOrder = false;
	let productsExpiringWithinXDaysCount = 0; // if productsExpiringWithinXDaysCount > 0 show dialog but allow to "confirm anyway"

	if (!expiryDatesArray?.length) {
		hasExpiryDatesNotFilledCount++;
	}
	else {
		let previousKnownDate = null;
		expiryDatesArray.forEach((date, index) => {
			if (!date) {
				// expiry dates not filled
				hasExpiryDatesNotFilledCount++;
			}
			else {
				const expiryDate = getDayMoment(date);
				if (!expiryDate.isValid()) {
					hasExpiryDatesNotFilledCount++;
					return;
				}

				// different between today and the expiryDate
				const daysDiff = parseInt(expiryDate.diff(currentDate, 'days'), 10);
				if (daysDiff < 1) {
					if (daysDiff === 0) {
						// product that expire today
						expireTodayCount++;
					}
					else {
						// product that expired
						expiredProductsCount++;
					}
				}
				else if (daysDiff <= tenantSettings?.expiryDateThresholds?.[0]) {
					productsExpiringWithinXDaysCount++;
				}

				// Check if products are placed in correct order
				if (index > 0 && previousKnownDate && expiryDate.isBefore(previousKnownDate)) {
					productsPlacedInIncorrectOrder = true;
				}
				previousKnownDate = expiryDate;
			}

		});
	}

	return {
		expiredProductsCount,
		expireTodayCount,
		hasExpiryDatesNotFilledCount,
		productsExpiringWithinXDaysCount,
		productsPlacedInIncorrectOrder,
	};
};


export const countProductsToRemoveOnNextService = (
	planogramItemVendRate,
	averageDaysSinceLastService,
	expiryDatesObj = {},
	{ referenceDate, timezone, isMarket = false } = {},
) => {
	// If no expiry dates, nothing to check
	if (!Object.keys(expiryDatesObj).length) {
		return 0;
	}

	// Get the current date
	const currentDate = timezone
		? getLocalTime(timezone, referenceDate || new Date()).startOf('day')
		: moment(referenceDate || undefined).startOf('day');

	// Only check products that could expire before next service
	const expiryDatesBeforeNextService = Object.entries(expiryDatesObj)
		.map(([selection, expiryDate]) => ({
			selection: Number(selection),
			expiryDate,
		}))
		.filter(({ selection, expiryDate }) => (
			Number.isInteger(selection) &&
			selection > 0 &&
			(!isMarket || selection === 1) &&
			Boolean(expiryDate)
		))
		.sort((left, right) => left.selection - right.selection)
		.filter(({ expiryDate }) => {
			const expiryMoment = timezone
				? getLocalTime(timezone, expiryDate).startOf('day')
				: moment(expiryDate).startOf('day');
			const daysUntilExpiry = expiryMoment.diff(currentDate, 'days');
			return daysUntilExpiry <= averageDaysSinceLastService;
		});

	if (isMarket) {
		// Market customers can reorder products, so a predicted sale cannot prove
		// that the oldest recorded product will be the one sold before expiry.
		return expiryDatesBeforeNextService.length ? 1 : 0;
	}

	// Get the vend rate for this planogram item
	const vendRate = planogramItemVendRate || 0;


	// count items that will be sold before next service
	const itemsThatWillBeSoldBeforeNextService = expiryDatesBeforeNextService.reduce((acc, {
		expiryDate,
		selection,
	}) => {
		const expiryMoment = timezone
			? getLocalTime(timezone, expiryDate).startOf('day')
			: moment(expiryDate).startOf('day');
		const daysUntilExpiry = expiryMoment.diff(currentDate, 'days');

		// how many items will be sold to the current expiryDate
		const predictedSalesToTheDate = Math.floor(vendRate * daysUntilExpiry);

		// if more items than is in the queue then add 1 to the sold before service count
		if (predictedSalesToTheDate >= selection) {
			return acc + 1;
		}

		return acc;
	}, 0);

	// items that should be removed
	const numberOfProductsToRemove = Math.max(0, expiryDatesBeforeNextService.length - itemsThatWillBeSoldBeforeNextService);

	return numberOfProductsToRemove;
};

export const shouldShowExpiryBeforeNextServiceWarning = ({ tenantSettings, product }) => {
	if (!product?.isFreshFood) {
		return true;
	}

	// AVC may manage fresh-food removal decisions manually, so this warning can
	// be disabled while expiry-date capture and hard expiry blockers stay active.
	return tenantSettings?.showFreshFoodExpiryBeforeNextServiceWarning !== false;
};
